import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { PencilIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import Tabs from '../../components/Tabs';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Shell/Modal';
import { Button } from '../../components/Button';
import {
	TemplateField,
	TemplateDate,
	TemplateReactSelect,
	TemplateTextarea,
} from '../../components/Attributes/Attributes_old';
import { getUserFullName } from '../../components/helpers/functions';
import { AvatarGenerated } from '../../components/Avatar';

import useAsync from '../../hooks/useAsync';
import usePrevious from '../../hooks/usePrevious';

import {
	getOrganization,
	getOrganizationUsers,
	getOrganizationServiceReports,
	getAssetsAdmin,
	createBlankAdminServiceReport,
	updateOrganization,
} from '../../api/admin';

const TYPE_OPTIONS = [
	{ label: 'Repair', value: 'repair' },
	{ label: 'Preventive Maintenance', value: 'pm' },
	{ label: 'Inspection', value: 'inspection' },
];

const DEFAULT_FORM = {
	completedOn: new Date(),
};

const Organization = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [isCreateModalOpen, setCreateModalOpen] = useState(false);
	const [form, setForm] = useState(DEFAULT_FORM);
	const [isEditing, setIsEditing] = useState(false);
	const [editForm, setEditForm] = useState({});

	console.log('IDID:', id);
	const {
		value: organization,
		loading: orgLoading,
		execute: refreshOrganization,
	} = useAsync(getOrganization, { defaultPayload: id });

	const { execute: onUpdateOrganization, loading: updateLoading } = useAsync(
		updateOrganization,
		{ immediate: false },
	);

	const { value: users, loading: usersLoading } = useAsync(
		getOrganizationUsers,
		{ defaultPayload: id },
	);

	const { value: serviceReports, loading: reportsLoading } = useAsync(
		getOrganizationServiceReports,
		{ defaultPayload: id },
	);

	const {
		value: assets,
		execute: onRefreshAssets,
		resetValue: resetAssets,
	} = useAsync(getAssetsAdmin, { immediate: false });

	const { execute: onCreateServiceReport, loading: createLoading } = useAsync(
		createBlankAdminServiceReport,
		{ immediate: false },
	);

	useEffect(() => {
		if (id) {
			onRefreshAssets(id);
		}
	}, [id]);

	const prevModalOpen = usePrevious(isCreateModalOpen);
	useEffect(() => {
		if (!isCreateModalOpen && prevModalOpen) {
			setForm(DEFAULT_FORM);
			resetAssets();
		}
	}, [isCreateModalOpen]);

	const ASSET_OPTIONS = assets?.map((asset) => ({
		label: `${asset?.manufacturer?.name || ''} ${asset?.model?.name || ''} (S/N: ${asset?.serialNumber || '-'})`,
		value: asset?.id,
		asset,
	}));

	const selectedAsset =
		ASSET_OPTIONS?.find((o) => o.value === form?.asset) || null;

	const handleOpenCreateModal = () => {
		setForm({
			...DEFAULT_FORM,
			customer: id,
			invoiceNumber: `${organization?.name?.substring(0, 3).toUpperCase()}-${Math.floor(100000 + Math.random() * 900000)}`,
		});
		setCreateModalOpen(true);
	};

	const handleCreateServiceReport = () => {
		onCreateServiceReport(
			{
				name: form?.name,
				asset: form?.asset,
				organizationId: form?.customer,
				fileName: form?.fileName,
				completedOn: form?.completedOn,
				type: form?.type,
				invoiceNumber: form?.invoiceNumber,
			},
			(payload) => {
				setCreateModalOpen(false);
				navigate(`/superadmin/service-report/${payload?.id}`);
			},
		);
	};

	console.log('organization: ', organization);

	return (
		<MainContent>
			<PageHeading
				onBack={() => navigate('/superadmin')}
				divider={false}
				actions={[
					{
						name: 'Create Service Report',
						onClick: handleOpenCreateModal,
					},
				]}
			>
				{organization?.name || 'Loading...'}
			</PageHeading>

			<Tabs
				tabs={[
					{ name: 'Overview', key: 'overview' },
					{ name: 'Users', key: 'users', count: users?.length || 0 },
					{
						name: 'Service Reports',
						key: 'reports',
						count: serviceReports?.length || 0,
					},
				]}
			>
				{(currentTab) => {
					if (currentTab === 'overview') {
						return (
							<div className="bg-white shadow rounded-lg">
								<div className="px-4 py-5 sm:p-6">
									<div className="flex items-center justify-between mb-4">
										<h3 className="text-lg font-medium leading-6 text-gray-900">
											Organization Details
										</h3>
										{!isEditing && (
											<button
												type="button"
												onClick={() => {
													setEditForm({
														name: organization?.name || '',
														address: organization?.address || '',
													});
													setIsEditing(true);
												}}
												className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
											>
												<PencilIcon className="-ml-0.5 h-4 w-4" />
												Edit
											</button>
										)}
									</div>

									{isEditing ? (
										<div className="space-y-4">
											<TemplateField
												name="Name"
												value={editForm?.name}
												onChange={(index, name) =>
													setEditForm({ ...editForm, name })
												}
											/>
											<TemplateTextarea
												name="Address"
												value={editForm?.address}
												onChange={(index, address) =>
													setEditForm({ ...editForm, address })
												}
												rows={4}
												placeholder="Enter address (multiple lines supported)"
											/>
											<div className="flex justify-end gap-x-3 mt-4">
												<Button
													type="secondary"
													onClick={() => setIsEditing(false)}
												>
													Cancel
												</Button>
												<Button
													onClick={() => {
														onUpdateOrganization(
															{
																id: organization?.id,
																name: editForm?.name,
																address: editForm?.address,
															},
															() => {
																console.log('replace: ', organization?.id);
																setIsEditing(false);
																refreshOrganization(organization?.id, null, {
																	overrideDefault: true,
																});
															},
														);
													}}
													disabled={updateLoading === 'pending'}
												>
													{updateLoading === 'pending' ? 'Saving...' : 'Save'}
												</Button>
											</div>
										</div>
									) : (
										<dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Name
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{organization?.name || '-'}
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Type
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{organization?.type || '-'}
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Status
												</dt>
												<dd className="mt-1">
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															organization?.active
																? 'bg-green-100 text-green-800'
																: 'bg-gray-100 text-gray-800'
														}`}
													>
														{organization?.active ? 'Active' : 'Inactive'}
													</span>
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Contract
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{organization?.contract || '-'}
												</dd>
											</div>
											<div className="sm:col-span-2">
												<dt className="text-sm font-medium text-gray-500">
													Address
												</dt>
												<dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
													{organization?.address || '-'}
												</dd>
											</div>
										</dl>
									)}
								</div>
							</div>
						);
					}

					if (currentTab === 'users') {
						return (
							<DataTable
								columns={[
									{
										header: '',
										accessorKey: 'avatar',
										enableSorting: false,
										cell: (info) => {
											const row = info?.row?.original;
											return (
												<AvatarGenerated
													size="xs"
													id={getUserFullName({
														firstName: row?.firstName,
														lastName: row?.lastName,
														...row,
													})}
												/>
											);
										},
									},
									{
										header: 'Name',
										accessorKey: 'name',
										cell: (info) => {
											const row = info?.row?.original;
											return getUserFullName(row) || 'Not provided';
										},
									},
									{
										header: 'Email',
										accessorKey: 'email',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Role',
										accessorKey: 'role.displayName',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Status',
										accessorKey: 'active',
										cell: (info) => (
											<span
												className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
													info?.getValue()
														? 'bg-green-100 text-green-800'
														: 'bg-gray-100 text-gray-800'
												}`}
											>
												{info?.getValue() ? 'Active' : 'Inactive'}
											</span>
										),
									},
								]}
								data={{ rows: users || [] }}
								usePagination={false}
								useExport={false}
								hasCheckboxes={false}
								manualSorting={false}
								loading={usersLoading === 'pending'}
							/>
						);
					}

					if (currentTab === 'reports') {
						return (
							<DataTable
								columns={[
									{
										header: 'Name',
										accessorKey: 'name',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Invoice #',
										accessorKey: 'invoiceNumber',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Type',
										accessorKey: 'type',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Status',
										accessorKey: 'status',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Completed On',
										accessorKey: 'completedOn',
										cell: (info) =>
											info?.getValue()
												? moment(info?.getValue()).format('MM/DD/YYYY')
												: '-',
									},
								]}
								data={{ rows: serviceReports || [] }}
								onRow={(row) =>
									navigate(`/superadmin/service-report/${row?.original?.id}`)
								}
								usePagination={false}
								useExport={false}
								hasCheckboxes={false}
								manualSorting={false}
								loading={reportsLoading === 'pending'}
								actions={[
									{
										label: 'Create Service Report',
										onClick: handleOpenCreateModal,
									},
								]}
							/>
						);
					}

					return null;
				}}
			</Tabs>

			<Modal
				title={`Create Service Report - ${organization?.name || ''}`}
				open={isCreateModalOpen}
				onClose={() => setCreateModalOpen(false)}
			>
				<div className="grid grid-cols-2 gap-4">
					<div className="col-span-2">
						<TemplateReactSelect
							name="Asset"
							options={ASSET_OPTIONS}
							value={selectedAsset}
							onChange={(index, asset) => {
								const reportName = `${organization?.name} ${asset?.asset?.manufacturer?.name || ''} ${asset?.asset?.model?.name || ''}`;
								setForm({
									...form,
									asset: asset?.value,
									name: reportName,
									fileName: `Scipods - ${reportName} - ${moment().format('MMDDYYYY')}.pdf`,
								});
							}}
						/>
					</div>

					<TemplateField
						name="Report Name"
						value={form?.name}
						onChange={(index, name) => setForm({ ...form, name })}
					/>

					<TemplateField
						name="File Name"
						value={form?.fileName}
						onChange={(index, fileName) => setForm({ ...form, fileName })}
					/>

					<TemplateDate
						name="Completed On"
						value={form?.completedOn}
						onChange={(index, completedOn) => setForm({ ...form, completedOn })}
					/>

					<TemplateReactSelect
						name="Type"
						options={TYPE_OPTIONS}
						value={TYPE_OPTIONS?.find((o) => o.value === form?.type) || null}
						onChange={(index, type) => setForm({ ...form, type: type?.value })}
					/>

					<TemplateField
						name="Invoice Number"
						value={form?.invoiceNumber}
						onChange={(index, invoiceNumber) =>
							setForm({ ...form, invoiceNumber })
						}
					/>

					<div className="col-span-2 flex justify-end mt-4">
						<Button
							onClick={handleCreateServiceReport}
							disabled={
								!form?.asset || !form?.name || createLoading === 'pending'
							}
						>
							{createLoading === 'pending'
								? 'Creating...'
								: 'Create Service Report'}
						</Button>
					</div>
				</div>
			</Modal>
		</MainContent>
	);
};

export default Organization;
